import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Chip,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Shield as SecurityIcon,
  Cloud as CloudIcon,
  TableChart as DatabaseIcon
} from '@mui/icons-material'
import { systemAPI } from '../services/api'

function SystemHealth() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [healthData, setHealthData] = useState({})
  const [systemInfo, setSystemInfo] = useState({})
  const [dbStats, setDbStats] = useState({})
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    loadSystemHealth()
  }, [])

  const loadSystemHealth = async () => {
    try {
      setLoading(true)
      setError('')

      const [healthResponse, infoResponse, dbResponse] = await Promise.allSettled([
        systemAPI.getHealth(),
        systemAPI.getInfo(),
        systemAPI.getDatabaseStats()
      ])

      if (healthResponse.status === 'fulfilled' && healthResponse.value.success) {
        setHealthData(healthResponse.value.data)
      }

      if (infoResponse.status === 'fulfilled' && infoResponse.value.success) {
        setSystemInfo(infoResponse.value.data)
      }

      if (dbResponse.status === 'fulfilled' && dbResponse.value.success) {
        setDbStats(dbResponse.value.data)
      }

      setLastUpdated(new Date())

    } catch (error) {
      console.error('Error loading system health:', error)
      setError('Failed to load system health data')
    } finally {
      setLoading(false)
    }
  }

  const handleClearCache = async () => {
    try {
      setError('')
      setSuccess('')
      
      const response = await systemAPI.clearCache()
      if (response.success) {
        setSuccess('Cache cleared successfully')
        await loadSystemHealth()
      } else {
        setError('Failed to clear cache')
      }
    } catch (error) {
      console.error('Error clearing cache:', error)
      setError('Failed to clear cache')
    }
  }

  const handleOptimizeDatabase = async () => {
    try {
      setError('')
      setSuccess('')
      
      const response = await systemAPI.optimizeDatabase()
      if (response.success) {
        setSuccess('Database optimized successfully')
        await loadSystemHealth()
      } else {
        setError('Failed to optimize database')
      }
    } catch (error) {
      console.error('Error optimizing database:', error)
      setError('Failed to optimize database')
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
      case 'online':
      case 'ok':
        return 'success'
      case 'warning':
      case 'slow':
        return 'warning'
      case 'error':
      case 'offline':
      case 'critical':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
      case 'online':
      case 'ok':
        return <CheckCircleIcon color="success" />
      case 'warning':
      case 'slow':
        return <WarningIcon color="warning" />
      case 'error':
      case 'offline':
      case 'critical':
        return <ErrorIcon color="error" />
      default:
        return <WarningIcon color="action" />
    }
  }

  const HealthCard = ({ title, status, value, description, icon, progress }) => (
    <Card className="hover-card">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, color: 'primary.main' }}>
              {icon}
            </Box>
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Chip
            label={status}
            color={getStatusColor(status)}
            size="small"
          />
        </Box>
        {value && (
          <Typography variant="h4" sx={{ mb: 1 }}>
            {value}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        {progress !== undefined && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={progress > 80 ? 'error' : progress > 60 ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {progress.toFixed(1)}% used
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )

  const ServiceStatus = ({ name, status, details }) => (
    <ListItem>
      <ListItemIcon>
        {getStatusIcon(status)}
      </ListItemIcon>
      <ListItemText
        primary={name}
        secondary={details}
      />
      <Chip
        label={status}
        color={getStatusColor(status)}
        size="small"
      />
    </ListItem>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">System Health</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSystemHealth}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={handleClearCache}
            sx={{ mr: 2 }}
          >
            Clear Cache
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleOptimizeDatabase}
          >
            Optimize DB
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {lastUpdated && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated: {lastUpdated.toLocaleString()}
        </Typography>
      )}

      {/* System Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <HealthCard
            title="System Status"
            status={healthData.overall_status || 'Unknown'}
            description="Overall system health"
            icon={<SecurityIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HealthCard
            title="Memory Usage"
            status={systemInfo.memory_usage > 80 ? 'Critical' : 'Healthy'}
            value={`${(systemInfo.memory_usage || 0).toFixed(1)}%`}
            description="Current memory consumption"
            icon={<MemoryIcon />}
            progress={systemInfo.memory_usage || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HealthCard
            title="Disk Usage"
            status={systemInfo.disk_usage > 80 ? 'Critical' : 'Healthy'}
            value={`${(systemInfo.disk_usage || 0).toFixed(1)}%`}
            description="Available disk space"
            icon={<StorageIcon />}
            progress={systemInfo.disk_usage || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <HealthCard
            title="Response Time"
            status={healthData.avg_response_time > 1000 ? 'Slow' : 'Fast'}
            value={`${healthData.avg_response_time || 0}ms`}
            description="Average API response time"
            icon={<SpeedIcon />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Services Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Services Status
              </Typography>
              <List>
                <ServiceStatus
                  name="Database"
                  status={healthData.database?.status || 'Unknown'}
                  details={`${healthData.database?.connections || 0} active connections`}
                />
                <Divider />
                <ServiceStatus
                  name="Web Server"
                  status={healthData.webserver?.status || 'Unknown'}
                  details={systemInfo.server_software || 'Unknown'}
                />
                <Divider />
                <ServiceStatus
                  name="PHP"
                  status={healthData.php?.status || 'Unknown'}
                  details={`Version ${systemInfo.php_version || 'Unknown'}`}
                />
                <Divider />
                <ServiceStatus
                  name="Cloudinary"
                  status={healthData.cloudinary?.status || 'Unknown'}
                  details={`${healthData.cloudinary?.usage || 0}% usage`}
                />
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <DatabaseIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">
                      {dbStats.total_tables || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tables
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <StorageIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h6">
                      {((dbStats.database_size || 0) / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Database Size
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6">
                      {dbStats.total_records || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Records
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6">
                      {dbStats.active_connections || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Connections
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* System Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Server Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>OS:</strong> {systemInfo.operating_system || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Server:</strong> {systemInfo.server_software || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>PHP Version:</strong> {systemInfo.php_version || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Uptime:</strong> {Math.floor((systemInfo.uptime || 0) / 3600)}h {Math.floor(((systemInfo.uptime || 0) % 3600) / 60)}m
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Performance
                  </Typography>
                  <Typography variant="body2">
                    <strong>CPU Usage:</strong> {(systemInfo.cpu_usage || 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2">
                    <strong>Memory Usage:</strong> {(systemInfo.memory_usage || 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2">
                    <strong>Load Average:</strong> {systemInfo.load_average || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Processes:</strong> {systemInfo.process_count || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Network & Security
                  </Typography>
                  <Typography variant="body2">
                    <strong>SSL Status:</strong> {systemInfo.ssl_enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Firewall:</strong> {systemInfo.firewall_status || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last Backup:</strong> {systemInfo.last_backup ? new Date(systemInfo.last_backup).toLocaleDateString() : 'Never'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Security Score:</strong> {systemInfo.security_score || 'N/A'}/100
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SystemHealth